import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import api, { apiError } from "../lib/api";
import { LoiField, applyToggles } from "../components/LoiField";

export default function LetterOfIntentFill({ formPath = "/contracts/loi/form", generatePath = "/contracts/loi/generate" } = {}) {
  const { id, resource } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [genBusy, setGenBusy] = useState(false);

  const loadForm = useCallback(() => {
    setLoading(true);
    api.get(`${formPath}?record_id=${id}`).then((r) => {
      setData(r.data);
      const init = {};
      (r.data.fields || []).forEach((f) => { init[f.id] = f.value || ""; });
      setValues(init);
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [id, formPath]);
  useEffect(() => { loadForm(); }, [loadForm]);

  const setVal = (f, v) => setValues((s) => ({ ...s, ...applyToggles(f, s[f.id], v), [f.id]: v }));
  const sections = [];
  (data?.fields || []).forEach((f) => {
    const t = f.section || "";
    const last = sections[sections.length - 1];
    if (last && last[0] === t) last[1].push(f); else sections.push([t, [f]]);
  });

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data: res } = await api.post(generatePath, { record_id: id, sections: values, name: data.name });
      const dl = await api.get(`/documents/${res.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement("a"); a.href = url; a.download = `${data.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Letter of Intent saved to Documents & downloaded");
    } catch (e) { toast.error(apiError(e)); } finally { setGenBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="loi-page">
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur px-4 sm:px-8 py-3">
        <div className="max-w-[820px] mx-auto flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/record/${resource}/${id}`)} data-testid="loi-back">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Back
          </Button>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <FileText className="h-4 w-4 text-primary" />{data?.name || "LOI – Purchase of Vacant Land"}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={generate} disabled={genBusy || loading || !data} data-testid="loi-generate">
              {genBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}Generate & Save PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !data ? (
          <div className="text-center py-24 text-muted-foreground">Could not load the letter of intent.</div>
        ) : (
          <div className="max-w-[820px] mx-auto space-y-4" data-testid="loi-body">
            <p className="text-xs text-muted-foreground">Fill in the business terms below, then Generate & Save. The letter uses the approved Arqitek design. Your edits are remembered on this record.</p>
            {sections.map(([title, fields]) => (
              <section key={title || "fields"} className={title ? "rounded-lg border border-border bg-card p-4 space-y-3" : "space-y-4"} data-testid={title ? `loi-section-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}` : undefined}>
                {title && <h2 className="text-sm font-semibold">{title}</h2>}
                <div className={title ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : "space-y-4"}>
                  {fields.map((f) => <LoiField key={f.id} f={f} value={values[f.id]} onChange={(v) => setVal(f, v)} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
